import React, { useState, useMemo, useEffect } from 'react';
import api from '../services/api';

// 3-step wizard for creating color×size variants of a product.
// On open, fetches existing variants (same product_code) and pre-checks/locks them so:
//   - already-opened colors/sizes appear pre-selected and can't be deselected
//   - already-existing (color, size) combos are skipped on creation
// Props:
//   stock        — base stock (must have id, color_id, size_id, product_code)
//   colors, sizes — full lookup arrays
//   onClose
//   onComplete   — called with the created list after successful run
export default function ColorSizeWizard({ stock, colors = [], sizes = [], onClose, onComplete, inline = false }) {
  const [step, setStep] = useState(1);
  const baseColorKey = stock?.color_id ? String(stock.color_id).toLowerCase() : null;
  const baseSizeKey  = stock?.size_id  ? String(stock.size_id).toLowerCase()  : null;

  const [lockedColors, setLockedColors] = useState(() => new Set(baseColorKey ? [baseColorKey] : []));
  const [lockedSizes, setLockedSizes]   = useState(() => new Set(baseSizeKey ? [baseSizeKey] : []));
  const [existingCombos, setExistingCombos] = useState(() => new Set()); // "colorKey|sizeKey"
  const [selectedColors, setSelectedColors] = useState(() => new Set(baseColorKey ? [baseColorKey] : []));
  const [selectedSizes, setSelectedSizes]   = useState(() => new Set(baseSizeKey ? [baseSizeKey] : []));
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // Mevcut varyantları çek: aynı product_code grubundaki tüm renk/beden ID'leri
  useEffect(() => {
    if (!stock?.id) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/stocks/${stock.id}/product-variants`);
        if (cancelled) return;
        if (r.data.success) {
          const d = r.data.data;
          const cs = new Set((d.color_ids || []).map(x => String(x).toLowerCase()));
          const ss = new Set((d.size_ids || []).map(x => String(x).toLowerCase()));
          const combos = new Set((d.combos || []).map(c => `${c.color_id || ''}|${c.size_id || ''}`));
          if (baseColorKey) cs.add(baseColorKey);
          if (baseSizeKey)  ss.add(baseSizeKey);
          setLockedColors(cs);
          setLockedSizes(ss);
          setSelectedColors(cs);
          setSelectedSizes(ss);
          setExistingCombos(combos);
        }
      } catch (e) { if (!cancelled) setErr(e.response?.data?.message || e.message); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [stock?.id, baseColorKey, baseSizeKey]);

  const toggleColor = (id) => {
    const key = String(id).toLowerCase();
    if (lockedColors.has(key)) return; // mevcut renkler kilitli
    setSelectedColors(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };
  const toggleSize = (id) => {
    const key = String(id).toLowerCase();
    if (lockedSizes.has(key)) return;
    setSelectedSizes(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const previewCombos = useMemo(() => {
    const cs = colors.filter(c => selectedColors.has(String(c.id).toLowerCase()));
    const ss = sizes.filter(s => selectedSizes.has(String(s.id).toLowerCase()));
    const out = [];
    const colorList = cs.length ? cs : [null];
    const sizeList = ss.length ? ss : [null];
    for (const c of colorList) {
      for (const s of sizeList) {
        const cKey = c ? String(c.id).toLowerCase() : '';
        const sKey = s ? String(s.id).toLowerCase() : '';
        const exists = existingCombos.has(`${cKey}|${sKey}`);
        out.push({ color: c, size: s, exists });
      }
    }
    return out;
  }, [colors, sizes, selectedColors, selectedSizes, existingCombos]);

  const newCount = previewCombos.filter(x => !x.exists).length;

  const finish = async () => {
    setBusy(true); setErr('');
    try {
      const colorIds = colors.filter(c => selectedColors.has(String(c.id).toLowerCase())).map(c => c.id);
      const sizeIds  = sizes.filter(s => selectedSizes.has(String(s.id).toLowerCase())).map(s => s.id);
      const r = await api.post(`/stocks/${stock.id}/wizard-variants`, { color_ids: colorIds, size_ids: sizeIds });
      if (r.data.success) {
        onComplete && onComplete(r.data);
      } else {
        setErr(r.data.message || 'Hata');
      }
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally { setBusy(false); }
  };

  const body = (
    <>
        <h3>Renk / Beden Sihirbazı — Adım {step} / 3</h3>

        <div style={{ marginBottom: 12, fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
          Ürün Kodu: <strong style={{ color: 'var(--accent-primary)' }}>{stock?.product_code || '—'}</strong>
        </div>

        {err && <div className="qlm-err">{err}</div>}

        {step === 1 && (
          <div>
            <div style={{ marginBottom: 10, fontSize: '0.85rem' }}>
              Bu üründe olacak <strong>renkleri</strong> seçin. 🔒 işaretli renkler zaten açılmış — kilitlidir, kaldırılamaz. Yeni renk eklemek için tıklayın.
            </div>
            <div className="csw-grid">
              {loading && <div className="qlm-empty">Mevcut varyantlar yükleniyor...</div>}
              {!loading && colors.length === 0 && <div className="qlm-empty">Renk tanımı yok</div>}
              {!loading && colors.map(c => {
                const key = String(c.id).toLowerCase();
                const isLocked = lockedColors.has(key);
                const checked = selectedColors.has(key);
                const cls = 'csw-chip' + (checked ? ' checked' : '') + (isLocked ? ' locked' : '');
                return (
                  <button type="button" key={c.id} className={cls} disabled={isLocked}
                    onClick={() => toggleColor(c.id)}
                    title={isLocked ? 'Bu renk zaten açılmış — kilitli' : (checked ? 'Tıkla: kaldır' : 'Tıkla: ekle')}>
                    <span className="csw-mark">{isLocked ? '🔒' : (checked ? '✓' : '')}</span>
                    <span className="csw-label">{c.code ? c.code + ' - ' : ''}{c.name}</span>
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop: 8, fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>
              Seçili: <strong style={{ color: 'var(--accent-primary)' }}>{selectedColors.size}</strong> renk ({lockedColors.size} mevcut + {selectedColors.size - lockedColors.size} yeni)
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <div style={{ marginBottom: 10, fontSize: '0.85rem' }}>
              Bu üründe olacak <strong>bedenleri</strong> seçin. 🔒 işaretli bedenler zaten açılmış — kilitlidir, kaldırılamaz. Yeni beden eklemek için tıklayın.
            </div>
            <div className="csw-grid">
              {loading && <div className="qlm-empty">Mevcut varyantlar yükleniyor...</div>}
              {!loading && sizes.length === 0 && <div className="qlm-empty">Beden tanımı yok</div>}
              {!loading && sizes.map(s => {
                const key = String(s.id).toLowerCase();
                const isLocked = lockedSizes.has(key);
                const checked = selectedSizes.has(key);
                const cls = 'csw-chip' + (checked ? ' checked' : '') + (isLocked ? ' locked' : '');
                return (
                  <button type="button" key={s.id} className={cls} disabled={isLocked}
                    onClick={() => toggleSize(s.id)}
                    title={isLocked ? 'Bu beden zaten açılmış — kilitli' : (checked ? 'Tıkla: kaldır' : 'Tıkla: ekle')}>
                    <span className="csw-mark">{isLocked ? '🔒' : (checked ? '✓' : '')}</span>
                    <span className="csw-label">{s.code ? s.code + ' - ' : ''}{s.name}</span>
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop: 8, fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>
              Seçili: <strong style={{ color: 'var(--accent-primary)' }}>{selectedSizes.size}</strong> beden ({lockedSizes.size} mevcut + {selectedSizes.size - lockedSizes.size} yeni)
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <div style={{ marginBottom: 10, fontSize: '0.85rem' }}>
              Toplam <strong>{previewCombos.length}</strong> kombinasyon: <span style={{color:'var(--accent-primary)',fontWeight:600}}>{newCount} yeni</span>, <span style={{color:'var(--text-tertiary)'}}>{previewCombos.length - newCount} mevcut (atlanır)</span>
            </div>
            <div className="qlm-list" style={{ maxHeight: 320 }}>
              {previewCombos.map((p, i) => (
                <div key={i} className="qlm-row" style={{ gridTemplateColumns: '1fr 1fr 110px' }}>
                  <div className="qlm-name-text">{p.color?.name || '—'}</div>
                  <div className="qlm-name-text">{p.size?.name || '—'}</div>
                  <div className="qlm-extra-text" style={{ textAlign: 'right' }}>
                    {p.exists
                      ? <span style={{ color: 'var(--text-tertiary)' }}>mevcut</span>
                      : <span style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>YENİ</span>}
                  </div>
                </div>
              ))}
            </div>
            {newCount === 0 && (
              <div style={{ marginTop: 10, padding: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 8, fontSize: '0.82rem' }}>
                ⚠ Yeni kombinasyon yok — Adım 1 ve 2'ye geri dönüp <strong>yeni renk veya beden</strong> ekleyin.
              </div>
            )}
          </div>
        )}

        <div className="modal-actions" style={{ justifyContent: 'space-between' }}>
          {onClose ? <button className="btn-ghost" onClick={onClose} disabled={busy}>Vazgeç</button> : <span/>}
          <div style={{ display: 'flex', gap: 8 }}>
            {step > 1 && <button className="btn-ghost" onClick={() => setStep(step - 1)} disabled={busy}>← Geri</button>}
            {step < 3 && <button className="btn-primary-sm" onClick={() => setStep(step + 1)} disabled={busy}>Sonraki →</button>}
            {step === 3 && <button className="btn-primary-sm" onClick={finish} disabled={busy || newCount === 0}>{busy ? 'Oluşturuluyor...' : `Son — ${newCount} varyant oluştur`}</button>}
          </div>
        </div>
    </>
  );

  if (inline) return <div className="csw-inline">{body}</div>;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box qlm-box" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
        {body}
      </div>
    </div>
  );
}
